import { useState, useCallback } from 'react';
import { seatingService } from '../../../lib/supabase';
import { createLogger } from '../../../lib/logger';

const log = createLogger('rsvp');

interface UseTableAssignmentParams {
  inviteCode: string;
  userId: string | undefined;
  seatingData: any;
  onRefresh: () => Promise<void>;
}

export function useTableAssignment({
  inviteCode,
  userId,
  seatingData,
  onRefresh,
}: UseTableAssignmentParams) {
  const [editingTableId, setEditingTableId] = useState<string | null>(null);

  const handleUpdateTable = useCallback(async (rsvpId: string, newTableId: number | null) => {
    if (!userId || !seatingData) return;

    try {
      log.debug(`handleUpdateTable: 開始更新 RSVP ${rsvpId} 到桌子 ${newTableId}`);

      const latestSeating = await seatingService.loadSeatingArrangement(inviteCode);
      log.debug('handleUpdateTable: 載入的最新座位數據', latestSeating);

      let tables: any[] = [];
      if (latestSeating) {
        if (Array.isArray(latestSeating)) {
          tables = [...latestSeating];
        } else if (latestSeating.tables && Array.isArray(latestSeating.tables)) {
          tables = [...latestSeating.tables];
        }
      }

      if (tables.length === 0) {
        log.debug('handleUpdateTable: 沒有現有桌子，創建預設桌子');
        tables = [
          { id: 1, name: 'Table 1', capacity: 8, guests: [] },
          { id: 2, name: 'Table 2', capacity: 8, guests: [] },
          { id: 3, name: 'Table 3', capacity: 8, guests: [] },
          { id: 4, name: 'Table 4', capacity: 8, guests: [] }
        ];
      }

      tables.forEach((table: any) => {
        if (table.guests && Array.isArray(table.guests)) {
          table.guests = table.guests.filter((guest: any) => {
            const guestId = typeof guest === 'string' ? guest : guest.id;
            return guestId !== rsvpId;
          });
        }
      });

      if (newTableId !== null) {
        const targetTable = tables.find((table: any) => table.id === newTableId);
        if (targetTable) {
          if (!targetTable.guests) {
            targetTable.guests = [];
          }
          targetTable.guests.push(rsvpId);
          log.debug(`handleUpdateTable: 將 RSVP ${rsvpId} 添加到桌子 ${targetTable.name}`);
        } else {
          log.error(`handleUpdateTable: 找不到桌子 ID ${newTableId}`);
        }
      }

      await seatingService.saveSeatingArrangement(inviteCode, tables);
      await onRefresh();

      setEditingTableId(null);
      log.info('handleUpdateTable: 更新完成');
    } catch (error) {
      log.error('Error updating table assignment:', error);
      alert('更新桌號失敗，請稍後再試');
    }
  }, [userId, inviteCode, seatingData, onRefresh]);

  const getAvailableTables = useCallback(() => {
    if (!seatingData) return [];

    let tables: any[] = [];
    if (Array.isArray(seatingData)) {
      tables = seatingData;
    } else if (seatingData.tables && Array.isArray(seatingData.tables)) {
      tables = seatingData.tables;
    }

    return tables.map((table: any) => ({
      id: table.id,
      name: table.name || `桌次 ${table.id}`
    }));
  }, [seatingData]);

  return {
    editingTableId,
    setEditingTableId,
    handleUpdateTable,
    getAvailableTables,
  };
}
